export interface ScheduleInput {
  scheduleId: string;
  campaignId: string;
  pdvId: string;
  weekdays: number[];
  /** Fechas sueltas (YYYY-MM-DD) que se suman al patrón semanal. Puede haberlas sin ningún weekday. */
  extraDates: string[];
  /**
   * Vigencia del horario (0304). `null` = desde el inicio / hasta el fin del rango que se expande.
   * Es OBLIGATORIA a propósito, igual que `extraDates`: así el typecheck marca a todo el que arma un
   * horario y nadie lo vuelve a expandir sobre la campaña entera por olvido.
   */
  vigenteDesde: string | null;
  vigenteHasta: string | null;
  startTime: string;
  endTime: string;
  headcount: number;
  rateAmount: number;
  rateUnit: 'jornada' | 'hora';
}

export interface PositionRow {
  scheduleId: string;
  campaignId: string;
  pdvId: string;
  date: string;
  startTime: string;
  endTime: string;
  slotIndex: number;
  rateAmount: number;
  rateUnit: 'jornada' | 'hora';
}

/** Día ISO (1=lunes … 7=domingo) de una fecha YYYY-MM-DD.
    Parseo manual a mediodía UTC: getUTCDay() es estable sin importar el timezone
    del sistema (new Date('YYYY-MM-DD') sería medianoche UTC, que en Lima UTC-5
    cae el día anterior y correría todos los días de semana en -1). */
export function isoWeekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  const utcDay = new Date(Date.UTC(y, m - 1, d, 12, 0, 0)).getUTCDay(); // 0=domingo … 6=sábado
  return utcDay === 0 ? 7 : utcDay;
}

/** Fecha siguiente en formato YYYY-MM-DD (también a mediodía UTC). */
function nextDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1, 12, 0, 0));
  const yy = next.getUTCFullYear();
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(next.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/**
 * Expande un horario a posiciones concretas dentro de [rangeStart, rangeEnd] (YYYY-MM-DD, inclusivo),
 * recortado a su vigencia: [max(rangeStart, vigenteDesde), min(rangeEnd, vigenteHasta)]. Un horario
 * dado de baja desde el primer día de su vigencia (hasta = desde − 1) no produce ninguna.
 */
export function expandSchedule(s: ScheduleInput, rangeStart: string, rangeEnd: string): PositionRow[] {
  const out: PositionRow[] = [];
  // Comparación lexicográfica, válida en YYYY-MM-DD.
  const desde = s.vigenteDesde !== null && s.vigenteDesde > rangeStart ? s.vigenteDesde : rangeStart;
  const hasta = s.vigenteHasta !== null && s.vigenteHasta < rangeEnd ? s.vigenteHasta : rangeEnd;
  if (desde > hasta) return out;
  const wanted = new Set(s.weekdays);
  // Se recorre por FECHA, así que una fecha suelta que además cae en el patrón se procesa una
  // sola vez, y una fuera del rango (o de la vigencia) nunca se visita.
  const sueltas = new Set(s.extraDates);
  for (let date = desde; date <= hasta; date = nextDate(date)) {
    if (!wanted.has(isoWeekday(date)) && !sueltas.has(date)) continue;
    for (let slot = 1; slot <= s.headcount; slot++) {
      out.push({
        scheduleId: s.scheduleId,
        campaignId: s.campaignId,
        pdvId: s.pdvId,
        date,
        startTime: s.startTime,
        endTime: s.endTime,
        slotIndex: slot,
        rateAmount: s.rateAmount,
        rateUnit: s.rateUnit,
      });
    }
  }
  return out;
}
